<script setup>
const { globalData } = useTTCState();
const content = ref(globalData.value.jobs);

const props = defineProps({
  postingUuid: String,
  jobId: String, // for tracking
  jobTitle: String, // for tracking
  buttonTheme: {
    type: String,
    default: "reversed",
  },
  videoUploadEnabled: {
    // custom SR application attributes determines if these are required/allowed
    type: Boolean,
    default: false,
  },
  letterUploadEnabled: {
    // Setting present in the Job API data(custom SR properties "{"id": "61365489501baf7e3c23dac5","label": "Letter of Motivation","value": {"id": "8f3d4e73-1674-47e8-9284-ee17ed471b6e","label": "Yes - mandatory"}}").. Not implemented..
    type: Boolean,
    default: true,
  },
  resumeUploadEnabled: {
    // custom SR application attributes determines if these are required/allowed
    type: Boolean,
    default: true,
  },
  unsolicited: [Boolean, null],
});

// ------ SMART RECRUITERS STUFF ------

const emailValue = ref(null);
const emailConfirmValue = ref(null);
const emailConfirmError = computed(
  () =>
    emailValue.value !== "" &&
    emailConfirmValue.value !== "" &&
    emailConfirmValue.value !== null &&
    emailValue.value !== emailConfirmValue.value,
);

const formLoading = ref(false);
const formSuccess = ref(false);
const formError = ref(false);
const submitURL = computed(
  () => `/api/smartrecruiters/postings/${props.postingUuid}/candidates`,
);

const { acceptedMimeTypes, getApplication } = useSmartRecruiters();

const { data, error: smartDataError } = await getApplication(
  props.postingUuid,
);

if (smartDataError.value) {
  console.error("sr endpoint smartDataError: ", smartDataError.value);
}

const implementPrivacyPolicyUrl = computed(() => {
  return data.value?.privacyPolicies[1]?.url || "#";
});

const avatarUploadEnabled = computed(
  // () => data.value?.settings.avatarUploadAvailable === true || false
  () => false,
); // decided by application settings(SR administration)

let isFilesReady = true;
const filesCollection = ref({
  addiatt: [], // multiple allowed
  resume: [], // single allowed
  avatar: [], // single allowed
  letter: [], // single allowed, gets joined into 'addiatt' before sending application
  certificates: [], // multiple allowed, gets joined into 'addiatt' before sending application
});

const handleFileInputChange = (evt) => {
  isFilesReady = false;
  evt.target.parentNode.classList.add("-processing");

  const inputKey = evt.target.name;
  const files = evt.target.files;

  const filePromises = Object.entries(files).map((item) => {
    return new Promise((resolve, reject) => {
      const [index, file] = item;

      // Check for file size limit (2MB = 2 * 1024 * 1024 bytes)
      const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
      if (file.size > MAX_FILE_SIZE) {
        alert(
          `File size exceeds the limit of 2MB. Please upload a smaller file.`,
        );
        reject();
        return;
      }

      if (!acceptedMimeTypes(file.type)) {
        alert(
          "File type not allowed. Accepted file types: PDF, DOC(X), RTF, JPG, or PNG. (Max 2MB)",
        );
        console.log("File processing canceled due to unsupported file type");
        reject();
        return;
      }

      // Sanitize file name
      const newFileName = file.name.replaceAll(" ", "_").trim();
      if (
        handleDuplicateFile({
          id: inputKey,
          fileName: newFileName,
          size: file.size,
        })
      ) {
        alert(`File already in list: ${file.name}`);
        reject();
        return;
      }

      const reader = new FileReader();
      reader.readAsBinaryString(file);

      reader.onload = function (event) {
        filesCollection.value[inputKey].push({
          fileName: newFileName,
          size: file.size,
          type: file.type,
          fileContentRaw: `${window.btoa(event.target.result)}`,
        });
        resolve();
      };

      reader.onerror = function (error) {
        console.error(error);
        alert(`Unable to read the file. Please try again.`);
        reject();
      };
    });
  });

  Promise.all(filePromises)
    .then(() => {
      isFilesReady = true;
      evt.target.parentNode.classList.add("has-content");
      evt.target.parentNode.classList.remove("-processing");
    })
    .catch((error) => {
      evt.target.parentNode.classList.remove("-processing", "has-content");
      evt.target.value = ""; // Reset the input on error
      evt.preventDefault();
    });
};

const handleFileRemoval = (obj) => {
  filesCollection.value[obj.id] = filesCollection.value[obj.id].filter(
    (file) => file.fileName !== obj.fileName && file.size !== obj.size,
  );
};

const handleDuplicateFile = (obj) => {
  const found = filesCollection.value[obj.id].filter(
    (file) => file.fileName === obj.fileName && file.size === obj.size,
  );
  return !!found.length;
};

// HANDLE SUBMIT ---------------------------------
const handleSubmit = (e) => {
  e.preventDefault();

  if (!isFilesReady) {
    alert("Submitted files are still processing");
    console.log("Submitted files still getting processed");
    return;
  }

  if (emailConfirmError.value) {
    alert("Email addresses are not matching");
    return;
  }

  if (filesCollection.value.resume.length === 0) {
    // required but single
    alert("Resumé not added");
    return;
  }
  if (filesCollection.value.certificates.length === 0) {
    // COMMENT: required but multiple allowed
    alert("Certificates not added");
    return;
  }

  const form = document.forms.jobApplicationForm;
  const formData = new FormData(form);

  // ------------ Setup initial info for application -----
  const applicationObject = {
    firstName: formData.get("firstname"),
    lastName: formData.get("lastname"),
    email: formData.get("email"),
    phoneNumber: formData.get("phone"),
    location: {
      city: formData.get("address"),
    },
    createJoinLink: false,
    consent: true,

    attachments: [
      //   {
      //     fileName: 'cover_letter.pdf', // display name
      //     mimeType: 'application/pdf',
      //     fileContent: '{base64 encoded file content}'
      //   }
    ],
    // messageToHiringManager: "Job application from new web site.. sorry for spam..",
    answers: [],
  };

  if (filesCollection.value.avatar.length) {
    // COMMENT: spec; single file and not required
    applicationObject.avatar = {
      fileName: filesCollection.value.avatar[0].fileName,
      mimeType: filesCollection.value.avatar[0].type,
      fileContent: filesCollection.value.avatar[0].fileContentRaw,
    };
  }

  if (!filesCollection.value.resume.length) {
    // COMMENT: spec; single and required
    // TODO: SHOW ERROR if applicable
  } else {
    applicationObject.resume = {
      fileName: filesCollection.value.resume[0].fileName,
      mimeType: filesCollection.value.resume[0].type,
      fileContent: filesCollection.value.resume[0].fileContentRaw,
    };
  }

  if (!filesCollection.value.certificates.length) {
    // COMMENT: spec; multiple, at least one required
    // TODO: SHOW ERROR if applicable
  } else {
    filesCollection.value.certificates.forEach((certificate) => {
      applicationObject.attachments.push({
        fileName: certificate.fileName,
        mimeType: certificate.type,
        fileContent: certificate.fileContentRaw,
      });
    });
  }
  if (!filesCollection.value.letter.length) {
    // COMMENT: spec; single and required
    // TODO: SHOW ERROR if applicable
  } else {
    applicationObject.attachments.push({
      fileName: filesCollection.value.letter[0].fileName,
      mimeType: filesCollection.value.letter[0].type,
      fileContent: filesCollection.value.letter[0].fileContentRaw,
    });
  }
  if (!filesCollection.value["addiatt"].length) {
    // COMMENT: spec; multiple and not required
    // TODO: SHOW ERROR if applicable if applicable
  } else {
    filesCollection.value["addiatt"].forEach((item) => {
      applicationObject.attachments.push({
        fileName: item.fileName,
        mimeType: item.type,
        fileContent: item.fileContentRaw,
      });
    });
  }

  // ------------ Collect info for application -----
  for (const pair of formData.entries()) {
    // loop through questions/answers
    if (
      pair[0] !== "firstname" && // outside answers response
      pair[0] !== "lastname" && // outside answers response
      pair[0] !== "email" && // outside answers response
      pair[0] !== "email-confirm" && // outside answers response
      pair[0] !== "address" && // outside answers response
      pair[0] !== "phone" && // outside answers response
      pair[0] !== "resume" && // outside answers response
      pair[0] !== "certificates" && // outside answers response
      pair[0] !== "letter" && // outside answers response
      pair[0] !== "addiatt" && // outside answers response
      pair[0] !== "avatar" && // outside answers response
      pair[0] !== "consent" && // outside answers response
      pair[1] !== "" // selected/written answer value not empty
    ) {
      applicationObject.answers.push({
        id: pair[0],
        records: [
          {
            fields: {
              value: [pair[1]],
            },
          },
        ],
      });
    }
  }

  formLoading.value = true;

  // Track application initiation/submission
  // gtm.trackEvent({
  //   event: "job_application_initiated",
  //   job_id: props.jobId,
  //   job_title: props.jobTitle,
  //   job_email: formData.get("email"),
  // });

  // POST JOB APPLICATION
  postApplication(applicationObject);
};

async function postApplication(data) {
  try {
    const response = await fetch(submitURL.value, {
      method: "POST",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(data),
    });

    const result = await response.json();
    // console.log('Success:', result)

    formLoading.value = false;
    formSuccess.value = true;
    formError.value = false;
    // gtm.trackEvent({
    //   event: "job_application_success",
    //   job_id: props.jobId,
    //   job_title: props.jobTitle,
    //   job_email: emailValue.value,
    // });

    // RESET APPLICATION
    document.forms.jobApplicationForm.reset();
    emailValue.value = "";
    emailConfirmValue.value = "";
    filesCollection.value.addiatt = [];
    filesCollection.value.resume = [];
    filesCollection.value.avatar = [];
    filesCollection.value.letter = [];
    filesCollection.value.certificates = [];
  } catch (error) {
    formLoading.value = false;
    formSuccess.value = false;
    formError.value = true;
    // gtm.trackEvent({
    //   event: "job_application_error",
    //   job_id: props.jobId,
    //   job_title: props.jobTitle,
    //   job_email: emailValue.value,
    //   job_error: error,
    // });
    console.error("Error:", error);
  }
}
</script>

<template>
  <div class="job-form text-s" v-if="!smartDataError">
    <div class="job-form__inner">
      <h3 class="job-form__heading mb-10 text-l">Apply now</h3>
      <form
        class="form"
        ref="formElement"
        v-if="data && data.questions"
        action="#"
        :class="{
          '-loading': formLoading,
        }"
        id="jobApplicationForm"
        @submit="handleSubmit($event)"
      >
        <ui-font-text type="s">Personnal info</ui-font-text>
        <!-- @input="handleFormChange" -->
        <div class="form-block">
          <div class="form-field ts-body-1 required">
            <input
              type="text"
              class="ts-body-1"
              placeholder="First name"
              id="firstname"
              name="firstname"
              required
            />
          </div>
          <div class="form-field ts-body-1 required">
            <input
              type="text"
              class="ts-body-1"
              placeholder="Last name"
              id="lastname"
              name="lastname"
              required
            />
          </div>
          <div class="form-field ts-body-1 required">
            <input
              type="email"
              class="ts-body-1"
              placeholder="Email"
              id="email"
              name="email"
              v-model="emailValue"
              required
            />
          </div>
          <div class="form-field ts-body-1 required">
            <input
              type="email"
              class="ts-body-1"
              placeholder="Confirm email"
              id="email-confirm"
              name="email-confirm"
              v-model="emailConfirmValue"
              required
            />
            <ui-font-text
              type="s"
              class="comparison-error"
              v-if="emailConfirmError"
            >
              Email adresses are not identical
            </ui-font-text>
          </div>
          <div class="form-field ts-body-1">
            <input
              type="text"
              class="ts-body-1"
              placeholder="Address & City"
              id="address"
              name="address"
            />
          </div>
          <div class="form-field ts-body-1">
            <input
              type="tel"
              class="ts-body-1"
              placeholder="Phone number"
              id="phone"
              name="phone"
            />
          </div>
        </div>

        <div v-for="question in data.questions" :key="question.id">
          <div
            class="question"
            v-if="question.fields[0].type !== 'INFORMATION'"
          >
            <label class="label ts-h6" :for="question.id">
              {{ question.label }}
            </label>

            <div
              class="form-field ts-body-1"
              v-for="field in question.fields"
              :key="field.id"
            >
              <div
                class="form-field__select"
                v-if="field.type === 'SINGLE_SELECT'"
              >
                <div class="icon">
                  <ui-icons-arrow-down />
                </div>
                <select
                  :name="question.id"
                  :id="question.id"
                  :required="field.required"
                  class="ts-body-1"
                >
                  <option value="">Please choose an option</option>
                  <option
                    v-for="option in field.values"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </div>

              <div
                class="form-field__radio-group"
                v-if="field.type === 'RADIO'"
              >
                <label
                  v-for="(fieldValue, fieldIndex) in field.values"
                  :key="fieldValue.id"
                  class="form-field__radio"
                >
                  <input
                    type="radio"
                    :name="question.id"
                    :value="fieldValue.id"
                    :checked="field.required ? fieldIndex === 0 : null"
                    :required="field.required"
                    :id="question.id + '-' + field.id"
                  />
                  <div class="form-field__radio-visuals"></div>
                  {{ fieldValue.label }}
                </label>
              </div>

              <div
                class="form-field__checkbox-group"
                v-if="field.type === 'CHECKBOX'"
              >
                <label
                  v-for="fieldValue in field.values"
                  :key="fieldValue.id"
                  class="form-field__checkbox custom-checkbox"
                >
                  <input
                    type="checkbox"
                    :required="field.required"
                    :name="question.id"
                    :value="fieldValue.id"
                    :id="field.id + '-' + fieldValue.id"
                  />
                  <div class="checkbox-visuals"></div>
                  {{ fieldValue.label }}
                </label>
              </div>
              <div
                class="form-field ts-body-1"
                v-if="field.type === 'INPUT_TEXT'"
                :class="{ required: field.required }"
              >
                <input
                  :id="question.id"
                  :name="question.id"
                  class="ts-body-1"
                  type="text"
                  :required="field.required"
                  @keyup="handleChange($event, 'text')"
                />
              </div>
              <div
                class="form-field ts-body-1"
                v-if="field.type === 'TEXTAREA'"
                :class="{ required: field.required }"
              >
                <textarea
                  :id="question.id"
                  :name="question.id"
                  :required="field.required"
                  @keyup="handleChange($event, 'textarea')"
                >
                </textarea>
              </div>
            </div>
          </div>
          <div v-else class="sr-information ts-body-1">
            {{ question.label }}
          </div>
        </div>

        <div class="form-block">
          <component-element-form-file
            v-bind="{
              label: 'Certificates',
              id: 'certificates',
              required: true,
              multiple: true,
              information:
                'Please upload all transcripts including specific courses and grades (e.g. both your bachelor’s and master’s degree). Accepted files: PDF, DOC(X), RTF, JPG or PNG. (Max 2MB)<br>Note: If you do not have a transcript in either English or the local language of the office you are applying to, then please upload the English translation of your transcript (included ECTS grades)',
            }"
            :files="filesCollection['certificates']"
            :key="'certificates'"
            @change="handleFileInputChange($event)"
            @remove="handleFileRemoval($event)"
          />
          <component-element-form-file
            v-bind="{
              label: 'Resume',
              id: 'resume',
              required: true,
              multiple: false,
              information:
                'Please upload CV (curriculum vitae) here. Accepted files: PDF, DOC(X), RTF, JPG or PNG. (Max 2MB)',
            }"
            :key="'resume'"
            :files="filesCollection['resume']"
            @change="handleFileInputChange($event)"
            @remove="handleFileRemoval($event)"
          />
          <component-element-form-file
            v-bind="{
              label: 'Letter of motivation',
              id: 'letter',
              required: true,
              multiple: false,
              information:
                'Please upload a motivational letter explaining which business areas you are interested in working with and why. Accepted files: PDF, DOC(X), RTF, JPG or PNG. (Max 2MB)',
            }"
            :key="'letter'"
            :files="filesCollection['letter']"
            @change="handleFileInputChange($event)"
            @remove="handleFileRemoval($event)"
          />
          <component-element-form-file
            v-bind="{
              label: 'Additional attachments',
              id: 'addiatt',
              required: false,
              multiple: true,
              information:
                'Accepted files: PDF, DOC(X), RTF, JPG or PNG. (Max 2MB)',
            }"
            :key="'addiatt'"
            :files="filesCollection['addiatt']"
            @change="handleFileInputChange($event)"
            @remove="handleFileRemoval($event)"
          />
          <component-element-form-file
            v-if="avatarUploadEnabled"
            v-bind="{
              label: 'Avatar upload',
              id: 'avatar',
              required: false,
              multiple: false,
              information:
                'Accepted files: PDF, DOC(X), RTF, JPG or PNG. (Max 2MB)',
            }"
            :files="filesCollection['avatar']"
            :key="'avatar'"
            @change="handleFileInputChange($event)"
            @remove="handleFileRemoval($event)"
          />
        </div>

        <div class="form-consent" v-if="!formSuccess">
          <label class="consent-checkbox">
            <div class="custom-checkbox">
              <input type="checkbox" id="consent" name="consent" required />
              <div class="checkbox-visuals"></div>
            </div>
            <!-- <span
              v-if="content.termsConditions"
              v-html="content.termsConditions"
              class="ts-body"
            ></span> -->
            <ui-font-text type="body" v-if="content.termsConditions">
              <span
                v-if="content.termsConditions"
                v-html="content.termsConditions"
              ></span>
            </ui-font-text>
          </label>
        </div>

        <div class="form-block is-success" v-if="formSuccess">
          <ui-font-text type="m" v-if="content.successText">
            <span v-html="content.successText"></span>
          </ui-font-text>
        </div>
        <div class="form-block" v-else>
          <div class="form-block-submit">
            <!-- <ui-call-to-action
              :button="true"
              :fullwidth="true"
              type="submit"
              :variant="'default'"
              class="submit"
            >
              <span v-if="formLoading">Sending application... </span>
              <span v-else>Send application</span>
            </ui-call-to-action> -->
            <ui-call-to-action
              :button="true"
              :is-big="true"
              aria-label="Apply button"
              :has-background="true"
              class="submit"
            >
              <ui-font-text type="s-bold" v-if="formStatus === 'loading'"
                >{{ formatCTALabel("Sending application...") }} /></ui-font-text
              >
              <ui-font-text type="s-bold" v-else>{{
                formatCTALabel("Send application")
              }}</ui-font-text>
            </ui-call-to-action>
            <div class="form-field-desc">
              <ui-font-text type="s">
                It may take several minutes to upload your files.
              </ui-font-text>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.form {
  --active-color: var(--current-theme);
  --inactive-color: hsla(0, 0%, 100%, 0.5);

  position: relative;
  padding-top: 40px;
  width: 100%;
  color: var(--white);

  &-consent {
    margin-top: 40px;
  }

  &.-loading {
    opacity: 0.5;
    pointer-events: none;
  }

  .loader {
    display: inline-block;
  }

  ::selection {
    background: var(--current-theme);
    color: var(--dark-grey);
  }

  input:not([type="radio"]):not([type="checkbox"]) {
    position: relative;
    width: 100%;
    background: none;
    border: none;
    padding: 15px 0;

    border-bottom: 1px solid var(--inactive-color);
    color: var(--white);
    transition: border 0.3s ease;
  }

  textarea:focus,
  input:not([type="radio"]):not([type="checkbox"]):focus {
    outline: none;
    border-bottom: 1px solid var(--active-color);
  }

  /* input:focus {
    opacity: 1;
  } */

  input[type="file"] {
    cursor: pointer;
  }

  input::placeholder {
    color: var(--inactive-color);
  }

  input:placeholder-shown {
    /* opacity: 0.5; */
  }

  /* textarea:focus,
  input:focus {
    opacity: 1;
  } */

  .form-block {
    position: relative;
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    gap: var(--grid-gap);

    @media (min-width: 768px) {
      grid-template-columns: repeat(2, 1fr);
    }

    & + & {
      margin-top: 40px;
    }

    &.-upload {
      @media (min-width: 768px) {
        grid-template-columns: repeat(1, 1fr);
      }

      & + & {
        margin-top: 20px;
      }
    }

    &.is-success {
      margin-top: 80px;
    }
  }

  .sr-information {
    position: relative;
    margin-block: 60px;
    font-style: italic;

    &:before {
      content: "NB.";
      display: inline-block;
    }
  }

  .form-block-submit {
    margin-top: 100px;

    .form-field-desc {
      margin-top: 10px;
    }
  }

  .question {
    margin-block: 60px;

    /* @media (--md){
      vertical-align: top;
      display: inline-block;
      width: calc(50% - var(--grid-gap));
      margin-right: var(--grid-gap);
    } */

    .label {
      display: block;
      margin-bottom: 10px;
    }
  }

  &-field {
    position: relative;
    width: 100%;
    margin-bottom: 5px;

    &.required {
      &::after {
        content: "*";
        position: absolute;
        top: 20px;
        right: 0;
        transition: opacity 0.3s ease;
      }
    }
  }

  &-field__radio {
    display: inline-block;
    position: relative;
    padding-left: max(22px, 1.5em);
    margin-right: max(28px, 2em);
    cursor: pointer;

    &:focus-within {
      text-decoration: underline;
    }

    &-visuals {
      position: absolute;
      top: 50%;
      left: 0;
      translate: 0 -50%;
      width: max(12px, 0.8em);
      height: max(12px, 0.8em);

      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        left: 0;
        width: max(12px, 0.8em);
        height: max(12px, 0.8em);
        translate: 0 -50%;
        border-radius: 50%;
        border: max(1px, 0.12em) solid var(--active-color);
        background-color: transparent;
      }

      &:after {
        left: 50%;
        width: max(6px, 0.4em);
        height: max(6px, 0.4em);
        translate: -50% -50%;
        border: 0;
        background-color: var(--active-color);
        opacity: 0;
        transition: opacity 0.25s ease;
      }
    }

    input {
      position: absolute;
      opacity: 0 !important;
      cursor: pointer;
    }

    /* input:focus + .form-field__radio-visuals:after,
    &:hover .form-field__radio-visuals:after {
      /* opacity: .5; * /
    } */

    input:checked + .form-field__radio-visuals:after {
      opacity: 1;
    }
  }

  .consent-checkbox {
    position: relative;
    display: block;
    padding-left: max(32px, 2em);
    cursor: pointer;

    .custom-checkbox {
      position: absolute;
      left: 0;
      top: 0;
      width: max(25px, 2em);
      height: max(25px, 2em);
    }

    /* &:hover .checkbox-visuals:after {
      /* opacity: .5; * /
    } */
  }

  .form-field__checkbox.custom-checkbox {
    display: block;
    padding-left: 25px;
    margin-right: 28px;
  }

  .custom-checkbox {
    position: relative;
    display: inline-block;
    cursor: pointer;

    .checkbox-visuals {
      position: absolute;
      top: 50%;
      left: 0;
      translate: 0 -50%;
      width: max(12px, 0.8em);
      height: max(12px, 0.8em);

      &:before,
      &:after {
        content: "";
        position: absolute;
        top: 50%;
        left: 0;
        width: max(12px, 0.8em);
        height: max(12px, 0.8em);
        translate: 0 -50%;
        border: max(1px, 0.12em) solid var(--active-color);
        background-color: transparent;
      }

      &:after {
        left: 50%;
        width: 100%;
        height: 100%;
        translate: -50% -50%;
        border: 0;
        background-color: var(--active-color);
        opacity: 0;
        transition: opacity 0.25s ease;
      }
    }

    input[type="checkbox"] {
      position: absolute;
      opacity: 0 !important;
      cursor: pointer;
    }

    /* input[type="checkbox"]:focus + .checkbox-visuals:after,
    &:hover .checkbox-visuals:after {
      /* opacity: .5; * /
    } */

    input[type="checkbox"]:checked + .checkbox-visuals:after {
      opacity: 1;
    }
    input[type="checkbox"]:focus-visible + .checkbox-visuals:before {
      outline: 2px solid hsla(0, 0%, 100%, 0.5);
    }
  }

  &-field__select {
    padding: 15px 0;
    border-bottom: 1px solid var(--inactive-color);
    transition: border 0.25s ease;

    &:focus-within {
      border-bottom: 1px solid var(--active-color);
    }

    select {
      display: block;
      width: 100%;
      color: var(--active-color);
      background-color: transparent;
      border: 0;
      cursor: pointer;
      min-height: 20px;
      appearance: none;
      -webkit-appearance: none;

      &:focus {
        outline: none;
      }

      option {
        color: var(--dark-grey);
      }
    }

    select:has(option[value=""]:checked) {
      color: var(--inactive-color);
    }

    .icon {
      position: absolute;
      top: 50%;
      right: 0;
      translate: 0 -50%;
      width: 1.5em;
      height: 1.5em;
      z-index: 2;
    }
  }

  &-field.-file {
    position: relative;

    input:not([type="radio"]):not([type="checkbox"]) {
      border-bottom: 0px;
    }

    &:after {
      content: "";
      position: absolute;
      left: 0;
      top: auto;
      bottom: 0;
      height: 1px;
      width: 100%;
      opacity: 0.2;
      background-color: var(--active-color);
      transition: opacity 0.25s ease;
    }
    &:before {
      content: "";
      position: absolute;
      left: 0;
      top: auto;
      bottom: 0;
      height: 1px;
      width: 100%;
      opacity: 0.2;
      translate: -100%;
      background-color: var(--active-color);
      display: none;
      animation: file-processing 1.4s linear infinite;
    }

    &:hover,
    &:focus-within,
    &.has-content {
      &:after {
        opacity: 1;
      }
    }

    &.-processing {
      overflow: hidden;
      cursor: default;
      pointer-events: none;

      input {
        opacity: 0.6;
      }

      &:before {
        /* file processing animation */
        display: block;
      }
    }

    .required-notation {
      position: absolute;
      top: 20px;
      right: 0;
      transition: opacity 0.3s ease;
    }
  }

  &-field-desc {
    color: var(--inactive-color);
  }
}

@keyframes file-processing {
  0% {
    translate: -100%;
  }
  100% {
    translate: 100%;
  }
}
</style>
